import client from "braintree-web/client";
import {BraintreeError} from "braintree-web/core";
import hostedFields, {HostedFieldsTokenizePayload} from "braintree-web/hosted-fields";
import classnames from "classnames";
import {Country} from "country-state-city";
import {useEffect, useMemo} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {BLACKLISTED_COUNTRIES, COUNTRIES_WITH_MANDATORY_STATES} from "@/pages/billing/payment-method/schemas.ts";

export const btFields = {
  number: {
    selector: "#card_number",
    placeholder: "0000 0000 0000 0000",
  },
  cvv: {
    selector: "#cvv",
    placeholder: "CVV",
  },
  expirationDate: {
    selector: "#expiry",
    placeholder: "MM / YYYY",
  },
};

export interface PaymentMethodCreditCardFormProps {
  onReady: (
    tokenizeFn: (
      options: object,
      callback: (err: BraintreeError, payload?: HostedFieldsTokenizePayload) => void
    ) => void
  ) => void;
  onError: (errors: string[]) => void;
  braintreeToken: string;
  hostedFieldErrors: Record<string, string>;
  variant?: "default" | "narrow";
  hasRequiredFields?: boolean;
}

export const PaymentMethodCreditCardForm = ({
  onReady,
  braintreeToken,
  hostedFieldErrors,
  variant = "default",
  onError,
  hasRequiredFields = true,
}: PaymentMethodCreditCardFormProps) => {
  const {watch} = useFormContext();
  const allCountries = useMemo(
    () => Country.getAllCountries().filter((country) => !BLACKLISTED_COUNTRIES.includes(country.isoCode)),
    []
  );
  const countryOptions = useMemo(() => allCountries.map((c) => ({label: c.name, value: c.isoCode})), [allCountries]);
  const selectedCountry = watch("country");
  const postalCodeRequired = COUNTRIES_WITH_MANDATORY_STATES.includes(selectedCountry);
  useEffect(() => {
    client.create({authorization: braintreeToken}, (createErr, clientInstance) => {
      if (createErr) {
        onError(["Our payment provider reported an error.", createErr.message]);
        console.error("Error initializing BT", createErr);
        return;
      }
      hostedFields.create(
        {
          client: clientInstance,
          fields: btFields,
          styles: {
            "@media (pointer: coarse)": {
              // to avoid auto-zoom on handheld devices
              input: {
                "font-size": "1rem",
              },
            },
          },
        },
        (err, hostedFieldsInstance) => {
          if (err || !hostedFieldsInstance) {
            if (err?.code === "HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME") {
              // do nothing, this is expected
            } else {
              onError(["Our payment provider reported an error.", err?.message || ""]);
              console.error("Error initializing Hosted fields", err);
            }
            return;
          }
          onReady(() => hostedFieldsInstance.tokenize.bind(hostedFieldsInstance));
        }
      );
    });
  }, [braintreeToken]);
  return (
    <>
      {hasRequiredFields && <RequiredFieldsNote />}
      <div className="row mb-3">
        <div className={variant === "narrow" ? "col-12 mb-3" : "col-5"}>
          <InputField
            name="cardholder_name"
            placeholder="First and Last Name on Card"
            label="Name on card"
            dataTestId="cardholder_name"
            required={true}
          />
        </div>
        <div className={variant === "narrow" ? "col-12 col-md mb-3 mb-md-2" : "col-5"}>
          <SelectField name="country" label="Country" options={countryOptions as SelectOption[]} required={true} />
        </div>
        {(variant === "default" || postalCodeRequired) && (
          <div className={variant === "narrow" ? "col-12 col-md-4" : "col-2"}>
            <InputField name="zipcode" placeholder="92545" label="Zip Code" required={postalCodeRequired} />
          </div>
        )}
      </div>
      <div className="row mb-3">
        <div className="col-12 col-md mb-3 mb-md-2">
          <FormLabel forId="card_number" required>
            Card number
          </FormLabel>
          <div
            className={classnames("bt-input form-control", {"is-invalid": hostedFieldErrors.number})}
            id="card_number"
          ></div>
          {hostedFieldErrors.number && <div className="invalid-feedback d-block">{hostedFieldErrors.number}</div>}
        </div>
        <div className="col-6 col-md-3">
          <FormLabel forId="expiry" required>
            MM / YYYY
          </FormLabel>
          <div
            className={classnames("bt-input form-control w-100", {"is-invalid": hostedFieldErrors.expirationDate})}
            id="expiry"
          ></div>
          {hostedFieldErrors.expirationDate && (
            <div className="invalid-feedback d-block">{hostedFieldErrors.expirationDate}</div>
          )}
        </div>
        <div className="col-6 col-md-3">
          <FormLabel forId="cvv" required>
            CVV
          </FormLabel>
          <div
            className={classnames("bt-input form-control w-100", {"is-invalid": hostedFieldErrors.cvv})}
            id="cvv"
          ></div>
          {hostedFieldErrors.cvv && <div className="invalid-feedback d-block">{hostedFieldErrors.cvv}</div>}
        </div>
      </div>
    </>
  );
};
